// Pure data layer for the GASS story library (ENG-853). No React, no I/O —
// resolve/normalize/dirty/validation-shape logic only, unit-tested. The panel
// calls convorcherAdmin for the network and this module for the shaping.

// The /admin/stories list route returns a bare array; the MCP wrapper returns
// { result }. Accept either (and never throw on junk).
function unwrap(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.result)) return res.result
  return []
}

// normalizeStoryList — map convorcher story rows to the flat shape the list
// renders, sorted by title then filename for a stable list.
export function normalizeStoryList(res) {
  return unwrap(res)
    .map((r) => ({
      filename: r.sourceFilename ?? r.filename ?? r.slug ?? r.id ?? '',
      id: r.id ?? '',
      title: r.title ?? '',
      version: String(r.version ?? ''),
      character: r.character ?? r.characterName ?? '',
      description: r.description ?? '',
      inDatabase: r.inDatabase ?? r.inDb ?? false,
      onDisk: r.onDisk ?? false,
    }))
    .sort(
      (a, b) =>
        (a.title || a.filename).localeCompare(b.title || b.filename) ||
        a.filename.localeCompare(b.filename),
    )
}

// storySelectOptions (ENG-875) — options for the Convorcher Session node's
// STORY dropdown. value = the story filename stem (what convorcher's ?story=
// resolver expects: "myra_v4" → stories/myra_v4.yaml). A current value that
// isn't in the fetched list is preserved as a flagged option rather than
// silently dropped — a stale/deleted story stays visible and selectable.
export function storySelectOptions(stories, current) {
  const opts = (stories ?? []).map((s) => ({
    value: s.filename,
    label: s.title || s.filename,
    missing: false,
  }))
  const cur = current ?? ''
  if (cur && !opts.some((o) => o.value === cur)) {
    opts.push({ value: cur, label: `${cur} (not in story list)`, missing: true })
  }
  return opts
}

// shapeValidation — normalize the /api/stories/validate response (always HTTP
// 200; the `valid` field carries the verdict) into a stable editor-facing shape.
// The server returns `errors: [{ path, message }]` (paths use "->"; no line
// numbers). Accept `issues` too for forward-compat. A missing/falsy `valid` is
// treated as invalid (loud, never optimistic).
export function shapeValidation(res) {
  const raw = Array.isArray(res?.errors) ? res.errors : Array.isArray(res?.issues) ? res.issues : []
  const issues = raw.map((i) => ({
    path: (i.path ?? '').replace(/->/g, ' → '),
    line: i.line ?? null,
    message: i.message ?? String(i),
  }))
  return { valid: res?.valid === true, issues }
}

// isDirty — editor buffer vs last-loaded source, whitespace-insensitive at the
// edges (a trailing newline is not a real edit).
export function isDirty(buffer, base) {
  return String(buffer ?? '').trim() !== String(base ?? '').trim()
}

// exportFilename — pull filename="…" (or bare filename=…) from a
// Content-Disposition header, else fall back to {filename}.yaml.
export function exportFilename(filename, contentDisposition) {
  const m = contentDisposition && /filename="?([^"]+)"?/.exec(contentDisposition)
  return (m && m[1]) || `${filename}.yaml`
}
